import { BottomDrawer } from '@/atoms/BottomDrawer';
import { Button } from '@/atoms/Button';
import { Checkbox } from '@/atoms/Checkbox';
import { Divider } from '@/atoms/Divider';
import { Input } from '@/atoms/Input';
import { Modal } from '@/atoms/Modal';
import { EMAIL_REGEX, PHONE_REGEX } from '@/constants';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';
import { BackendBookingDateType, EventType, Palettes, Variants } from '@/types';
import { parseText } from '@/utils/parseText';
import cn from 'classnames';
import Link from 'next/link';
import { Calendar } from '../Calendar';
import { useOneEventRegistration } from './hooks/useOneEventRegistration';
import styles from './OneEventForm.module.scss';

type OneEventFormProps = {
  events: EventType[];
  bookingDates: BackendBookingDateType[];
  className?: string;
  parentEvent: string;
  translations: Record<string, string>;
  locale?: string;
  common: Record<string, string>;
};

export function OneEventForm({
  events,
  bookingDates,
  className,
  parentEvent,
  translations,
  locale,
  common,
}: OneEventFormProps) {
  const wrapperClassName = cn(styles.wrapper, className);

  const { isMobile } = useAdaptiveWidth();
  const {
    formData,
    availableSeats,
    noSeatsPopup,
    handleClosePopup,
    handleDateChange,
    buttonDisabled,
    policyLink,
    rulesLink,
    register,
    handleSubmit,
    errors,
    setValue,
    fields,
    append,
    remove,
    onSubmit,
  } = useOneEventRegistration({
    events,
    bookingDates,
    parentEvent,
    locale,
    common,
  });

  const popupContent = (
    <div className={styles.popup}>
      <img src="/svg/danger.svg" alt="danger" className={styles.popupIcon} />
      <div className={styles.popupText}>
        <p className={styles.popupTitle}>
          Места на событие «{noSeatsPopup}» закончились
        </p>
        <p className={styles.popupDesc}>Вы не зарегистрированы на событие</p>
      </div>
      <Button
        text="Выбрать другое событие"
        onClick={handleClosePopup}
        className={styles.popupButton}
      />
    </div>
  );

  return (
    <form className={wrapperClassName} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.main}>
        <div className={styles.contacts}>
          <h4 className={styles.title}>{translations.formTitle}</h4>
          <div className={styles.inputs}>
            {fields.map((person, index) => (
              <div className={styles.inputsWrapper} key={person.id}>
                <div className={styles.row}>
                  <Input
                    register={register}
                    label={common.name}
                    name={`persons[${index}].name`}
                    placeholder={common.name}
                    required
                    pattern={/^[a-zа-я ,.'-]+$/i}
                    setValue={setValue}
                    error={errors.persons && errors.persons[index]?.name}
                  />
                  <Input
                    register={register}
                    label={common.lastName}
                    name={`persons[${index}].surname`}
                    placeholder={common.lastName}
                    pattern={/^[a-zа-я ,.'-]+$/i}
                    setValue={setValue}
                    error={errors.persons && errors.persons[index]?.surname}
                  />
                </div>
                <div className={styles.row}>
                  <Input
                    register={register}
                    label={common.phone}
                    name={`persons[${index}].phone`}
                    placeholder="+7 999 999 99 99"
                    setValue={setValue}
                    required={index === 0}
                    type="tel"
                    pattern={PHONE_REGEX}
                    maxLength={18}
                    error={errors.persons && errors.persons[index]?.phone}
                  />
                  <Input
                    register={register}
                    label={common.email}
                    name={`persons[${index}].email`}
                    pattern={EMAIL_REGEX}
                    required={index === 0}
                    placeholder="mail@mail.ru"
                    setValue={setValue}
                    error={errors.persons && errors.persons[index]?.email}
                  />
                </div>
                {index > 0 && (
                  <Button
                    variant={Variants.Outline}
                    palette={Palettes.Secondary}
                    text={translations.deleteUser}
                    onClick={() => remove(index)}
                    className={styles.buttonAdd}
                  />
                )}
              </div>
            ))}
          </div>
          <div className={styles.checkbox}>
            <div className={styles.checkboxWrapper}>
              <Checkbox
                register={register}
                label={
                  <>
                    {parseText(common.agreeTo)}
                    <Link
                      className={styles.highlight}
                      href={policyLink}
                      target="_blank"
                    >
                      {parseText(common.personalData)}
                    </Link>
                  </>
                }
                name="agreeToProcessingPersonalData"
                required
              />
              {errors.agreeToProcessingPersonalData && (
                <div className={styles.error}>{common.required}</div>
              )}
            </div>
            <div className={styles.checkboxWrapper}>
              <Checkbox
                register={register}
                label={
                  <>
                    {parseText(translations.agreeWith)}
                    <Link
                      className={styles.highlight}
                      href={rulesLink}
                      target="_blank"
                    >
                      {parseText(translations.rules)}
                    </Link>
                  </>
                }
                name="agreeToRules"
                required
              />
              {errors.agreeToRules && (
                <div className={styles.error}>{common.required}</div>
              )}
            </div>
          </div>
          <Button
            variant={Variants.Filled}
            text={translations.addUser}
            type="button"
            onClick={() =>
              append({ name: '', surname: '', phone: '', email: '' })
            }
            className={styles.buttonAdd}
          />
        </div>
        <div className={styles.date}>
          <Calendar
            onChange={handleDateChange}
            className={styles.calendar}
            availableDates={bookingDates.map((item) => ({
              date: new Date(item.date),
            }))}
            date={formData.date ?? new Date()}
            selectedDate={formData.date ?? undefined}
            noAutoChange
            preventVisualSelection
          />
          <div className={styles.calendarDivider}>
            <Divider />
          </div>
          <div className={styles.free}>
            <p className={styles.freeText}>
              {availableSeats === 0
                ? translations.noSeats
                : translations.seatsCount}
            </p>
            {availableSeats > 0 && (
              <p className={styles.freeNumber}>{availableSeats}</p>
            )}
          </div>
        </div>
      </div>
      <div className={styles.bottom}>
        <Button
          text={translations.signUp}
          onClick={() => {}}
          type="submit"
          className={styles.button}
          disabled={buttonDisabled}
        />
      </div>
      <Modal active={!!noSeatsPopup && !isMobile} closeFn={handleClosePopup}>
        {popupContent}
      </Modal>
      <BottomDrawer
        active={!!noSeatsPopup && !!isMobile}
        closeFn={handleClosePopup}
        drawerId="no-seats-popup-one-event"
        closeButton
      >
        {popupContent}
      </BottomDrawer>
    </form>
  );
}
